import { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { FormField, FormToggle } from '@/shared/components'
import { DatePeriodeRow } from './date-periode-row'
import type { Periode } from '@/shared/types/admin'
import type { PeriodType } from '@/shared/types/common'

const datePeriodeSchema = z.object({
  id: z.number().optional(),
  libelle: z.string().min(1, 'Le libellé est obligatoire'),
  dateDebut: z.string().min(1, 'La date de début est obligatoire'),
  dateFin: z.string().min(1, 'La date de fin est obligatoire'),
})

const schema = z.object({
  titre: z.string().min(1, 'Le titre est obligatoire'),
  typePeriode: z.string().optional(),
  actif: z.boolean(),
  datesPeriodes: z.array(datePeriodeSchema),
})

type FormValues = z.infer<typeof schema>

interface PeriodeFormProps {
  periode?: Periode
  onSubmit: (data: {
    titre: string
    typePeriode?: PeriodType
    actif: boolean
    datesPeriodes: Array<{ id?: number; libelle: string; dateDebut: string; dateFin: string }>
  }) => void
  isPending: boolean
}

export function PeriodeForm({ periode, onSubmit, isPending }: PeriodeFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { titre: '', typePeriode: '', actif: true, datesPeriodes: [] },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'datesPeriodes',
  })

  useEffect(() => {
    if (periode) {
      setValue('titre', periode.titre)
      setValue('typePeriode', periode.typePeriode || '')
      setValue('actif', periode.actif)
      if (periode.datesPeriodes?.length) {
        setValue('datesPeriodes', periode.datesPeriodes.map((dp) => ({
          id: dp.id,
          libelle: dp.libelle,
          dateDebut: dp.dateDebut,
          dateFin: dp.dateFin,
        })))
      }
    }
  }, [periode, setValue])

  const handleFormSubmit = (values: FormValues) => {
    onSubmit({
      titre: values.titre.trim(),
      typePeriode: (values.typePeriode as PeriodType) || undefined,
      actif: values.actif,
      datesPeriodes: values.datesPeriodes.map((dp) => ({
        ...(dp.id ? { id: dp.id } : {}),
        libelle: dp.libelle.trim(),
        dateDebut: dp.dateDebut,
        dateFin: dp.dateFin,
      })),
    })
  }

  const inputClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="mx-auto max-w-lg space-y-6">
      <div className="rounded-xl border border-neutral-200 bg-white p-6 space-y-5">
        <h2 className="text-base font-semibold text-neutral-900">
          {t('administration.sections.periods.form.informationTitle', 'Informations de la période')}
        </h2>

        <FormField
          label={t('administration.sections.periods.form.title', 'Titre de la période')}
          error={errors.titre?.message}
          helper={t('administration.sections.periods.form.titleHelper', 'Nom de la période (ex: Vacances, Entreprise, Ecole)')}
          required
        >
          <input {...register('titre')} className={inputClass} />
        </FormField>

        <FormField label={t('administration.sections.periods.form.type', 'Type de période')}>
          <select {...register('typePeriode')} className={inputClass}>
            <option value="">{t('administration.sections.periods.form.none', 'Aucun')}</option>
            <option value="ECOLE">{t('administration.sections.periods.form.school', 'École')}</option>
            <option value="VACANCES">{t('administration.sections.periods.form.vacation', 'Vacances')}</option>
            <option value="RELAX">{t('administration.sections.periods.form.relax', 'Relax')}</option>
          </select>
        </FormField>

        <FormToggle
          label={t('administration.sections.periods.form.active', 'Période active')}
          checked={watch('actif')}
          onChange={(v) => setValue('actif', v)}
        />
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-neutral-900">
            {t('administration.sections.periods.form.dates.title', 'Dates de période')}
          </h2>
          <button
            type="button"
            onClick={() => append({ libelle: '', dateDebut: '', dateFin: '' })}
            className="flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('administration.sections.periods.form.dates.add', 'Ajouter une date')}
          </button>
        </div>

        {fields.length === 0 && (
          <p className="py-4 text-center text-sm text-neutral-400">
            {t('administration.sections.periods.form.dates.none', 'Aucune date ajoutée. Cliquez sur "Ajouter une date" pour commencer.')}
          </p>
        )}

        <div className="space-y-3">
          {fields.map((field, index) => (
            <DatePeriodeRow
              key={field.id}
              index={index}
              register={register}
              errors={errors}
              onRemove={() => remove(index)}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/administration/periodes')}
          className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          {t('administration.sections.periods.form.cancel', 'Annuler')}
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending
            ? t('administration.sections.periods.form.saving', 'Enregistrement...')
            : periode
              ? t('administration.sections.periods.form.save', 'Enregistrer')
              : t('administration.sections.periods.form.create', 'Créer la période')}
        </button>
      </div>
    </form>
  )
}
